import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axiosInstance from "../api/axiosInstance";

const PAGE_SIZE = 10;
const TOAST_DURATION = 4000;

const api = {
  list: (search = "", notified = "", hasUser = "") => {
    const p = new URLSearchParams();
    if (search) p.append("q", search);
    if (notified !== "") p.append("notified", notified);
    if (hasUser !== "") p.append("has_user", hasUser);
    return axiosInstance.get(`/api/superadmin/contact-us/?${p}`);
  },
  get: (id) => axiosInstance.get(`/api/superadmin/contact-us/${id}/`),
};

const SearchIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const CloseIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const ChevronLeftIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
  >
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const ChevronRightIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);
const EyeIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const UserIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);
const MailIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 7-10 7L2 7" />
  </svg>
);
const PhoneIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.15 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.06 1.2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 9.13a16 16 0 0 0 5.6 5.6l1.2-1.2a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 20.92 16z" />
  </svg>
);
const BuildingIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <rect x="2" y="3" width="20" height="19" rx="1" />
    <path d="M9 21V9h6v12M9 3v6M15 3v6" />
  </svg>
);
const MessageIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);
const BellIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);
const CalendarIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

let _toastSetter = null;
const toast = {
  _push: (item) => {
    const id = Date.now() + Math.random();
    _toastSetter?.((p) => [...p, { id, ...item }]);
    return id;
  },
  success: (title, msg) => toast._push({ type: "success", title, msg }),
  error: (title, msg) => toast._push({ type: "error", title, msg }),
  info: (title, msg) => toast._push({ type: "info", title, msg }),
  loading: (title) => toast._push({ type: "loading", title, persistent: true }),
  dismiss: (id) => _toastSetter?.((p) => p.filter((t) => t.id !== id)),
};
const TOAST_META = {
  success: { bar: "#16a34a", icon: "✓" },
  error: { bar: "#dc2626", icon: "✕" },
  info: { bar: "#1d4ed8", icon: "i" },
  loading: { bar: "#94a3b8", icon: null },
};

const ToastItem = ({ t, onDismiss }) => {
  const meta = TOAST_META[t.type];
  const [progress, setProgress] = useState(100);
  const [exiting, setExiting] = useState(false);
  const rafRef = useRef(null);
  const startTs = useRef(Date.now());
  const dismiss = useCallback(() => {
    setExiting(true);
    setTimeout(() => onDismiss(t.id), 280);
  }, [t.id, onDismiss]);
  useEffect(() => {
    if (t.persistent) return;
    const tick = () => {
      const pct = Math.max(
        0,
        100 - ((Date.now() - startTs.current) / TOAST_DURATION) * 100,
      );
      setProgress(pct);
      if (pct > 0) rafRef.current = requestAnimationFrame(tick);
      else dismiss();
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [t.persistent, dismiss]);
  return (
    <div
      style={{
        position: "relative",
        overflow: "hidden",
        background: "#fff",
        borderRadius: 10,
        borderLeft: `4px solid ${meta.bar}`,
        boxShadow: "0 4px 20px rgba(0,0,0,0.10), 0 0 0 1px rgba(0,0,0,0.06)",
        minWidth: 300,
        maxWidth: 360,
        animation: exiting
          ? "toastOut 0.28s ease forwards"
          : "toastIn 0.28s cubic-bezier(0.34,1.56,0.64,1) forwards",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 10,
          padding: "13px 14px 14px",
        }}
      >
        <div
          style={{
            flexShrink: 0,
            width: 24,
            height: 24,
            borderRadius: "50%",
            background: `${meta.bar}18`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginTop: 1,
          }}
        >
          {t.type === "loading" ? (
            <svg
              width="13"
              height="13"
              viewBox="0 0 13 13"
              fill="none"
              style={{ animation: "spin 0.85s linear infinite" }}
            >
              <circle
                cx="6.5"
                cy="6.5"
                r="5"
                stroke={meta.bar}
                strokeWidth="2"
                strokeDasharray="8 20"
                strokeLinecap="round"
              />
            </svg>
          ) : (
            <span
              style={{
                fontSize: 11,
                fontWeight: 800,
                color: meta.bar,
                lineHeight: 1,
              }}
            >
              {meta.icon}
            </span>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            style={{
              margin: 0,
              fontSize: 13.5,
              fontWeight: 650,
              color: "#0f172a",
              lineHeight: 1.35,
            }}
          >
            {t.title}
          </p>
          {t.msg && (
            <p
              style={{
                margin: "3px 0 0",
                fontSize: 12.5,
                color: "#64748b",
                lineHeight: 1.5,
              }}
            >
              {t.msg}
            </p>
          )}
        </div>
        <button
          onClick={dismiss}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "#cbd5e1",
            padding: "2px",
            flexShrink: 0,
            display: "flex",
            borderRadius: 4,
            transition: "color 0.15s",
            marginTop: 1,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "#94a3b8")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "#cbd5e1")}
        >
          <CloseIcon />
        </button>
      </div>
      {!t.persistent && (
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 3,
            background: "#f1f5f9",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${progress}%`,
              background: meta.bar,
              transition: "width 0.1s linear",
              borderRadius: "0 2px 0 0",
            }}
          />
        </div>
      )}
    </div>
  );
};

const ToastContainer = () => {
  const [toasts, setToasts] = useState([]);
  _toastSetter = setToasts;
  const dismiss = useCallback(
    (id) => setToasts((p) => p.filter((t) => t.id !== id)),
    [],
  );
  if (!toasts.length) return null;
  return (
    <div
      style={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        alignItems: "flex-end",
      }}
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} t={t} onDismiss={dismiss} />
      ))}
    </div>
  );
};

const SkeletonRow = () => (
  <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
    {[40, 160, 170, 110, 120, 90, 90, 110, 80].map((w, i) => (
      <td key={i} style={{ padding: "14px 16px" }}>
        <div
          style={{
            height: 13,
            width: w,
            maxWidth: "100%",
            borderRadius: 6,
            background:
              "linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)",
            backgroundSize: "200% 100%",
            animation: "cmShimmer 1.4s infinite",
          }}
        />
      </td>
    ))}
  </tr>
);
const TableSkeleton = () => (
  <>
    {Array.from({ length: PAGE_SIZE }).map((_, i) => (
      <SkeletonRow key={i} />
    ))}
  </>
);

const Pagination = ({ currentPage, totalPages, totalCount, onPageChange }) => {
  if (totalPages <= 1) return null;
  const startItem = (currentPage - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * PAGE_SIZE, totalCount);
  const getPages = () => {
    if (totalPages <= 7)
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [1];
    if (currentPage > 3) pages.push("...");
    for (
      let i = Math.max(2, currentPage - 1);
      i <= Math.min(totalPages - 1, currentPage + 1);
      i++
    )
      pages.push(i);
    if (currentPage < totalPages - 2) pages.push("...");
    pages.push(totalPages);
    return pages;
  };
  const btnBase = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 34,
    height: 34,
    padding: "0 6px",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    background: "#fff",
    color: "#374151",
    transition: "all 0.15s",
    lineHeight: 1,
  };
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "14px 20px",
        borderTop: "1px solid #f1f5f9",
        background: "#f8fafc",
        flexWrap: "wrap",
        gap: 10,
      }}
    >
      <span style={{ fontSize: 13, color: "#64748b" }}>
        Showing{" "}
        <strong style={{ color: "#374151" }}>
          {startItem}–{endItem}
        </strong>{" "}
        of <strong style={{ color: "#374151" }}>{totalCount}</strong> results
      </span>
      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          style={{
            ...btnBase,
            opacity: currentPage === 1 ? 0.4 : 1,
            cursor: currentPage === 1 ? "not-allowed" : "pointer",
          }}
        >
          <ChevronLeftIcon />
        </button>
        {getPages().map((p, i) =>
          p === "..." ? (
            <span
              key={`e${i}`}
              style={{
                padding: "0 4px",
                color: "#94a3b8",
                fontSize: 13,
                lineHeight: "34px",
              }}
            >
              …
            </span>
          ) : (
            <button
              key={p}
              onClick={() => onPageChange(p)}
              className={p === currentPage ? "" : "cu-page-btn"}
              style={{
                ...btnBase,
                ...(p === currentPage
                  ? {
                      background: "#3b82f6",
                      color: "#fff",
                      border: "1px solid #3b82f6",
                      fontWeight: 600,
                    }
                  : {}),
              }}
            >
              {p}
            </button>
          ),
        )}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          style={{
            ...btnBase,
            opacity: currentPage === totalPages ? 0.4 : 1,
            cursor: currentPage === totalPages ? "not-allowed" : "pointer",
          }}
        >
          <ChevronRightIcon />
        </button>
      </div>
    </div>
  );
};

const Badge = ({
  active,
  trueLabel = "Yes",
  falseLabel = "No",
  trueColor = "#16a34a",
  falseBg = "#fee2e2",
  falseColor = "#dc2626",
  trueBg = "#dcfce7",
}) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      padding: "3px 10px",
      borderRadius: 20,
      fontSize: 12,
      fontWeight: 600,
      background: active ? trueBg : falseBg,
      color: active ? trueColor : falseColor,
    }}
  >
    <span
      style={{
        width: 6,
        height: 6,
        borderRadius: "50%",
        background: active ? "#22c55e" : "#f43f5e",
        display: "inline-block",
      }}
    />
    {active ? trueLabel : falseLabel}
  </span>
);

const Modal = ({ title, onClose, children, maxWidth = 560 }) => {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          width: "100%",
          maxWidth,
          boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
          animation: "cmFadeUp 0.2s ease",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 24px",
            borderBottom: "1px solid #f1f5f9",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: 17,
              fontWeight: 700,
              color: "#0f172a",
            }}
          >
            {title}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#94a3b8",
              display: "flex",
              padding: 4,
              borderRadius: 6,
              transition: "color 0.15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#475569")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#94a3b8")}
          >
            <CloseIcon />
          </button>
        </div>
        <div
          style={{
            padding: "20px 24px 24px",
            maxHeight: "80vh",
            overflowY: "auto",
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

const StatsBar = ({ contacts, loading }) => {
  const total = contacts.length;
  const notified = contacts.filter((c) => c.is_notified).length;
  const withUser = contacts.filter((c) => !!c.user).length;

  const Card = ({ label, count, color, bg, border }) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 18px",
        borderRadius: 10,
        background: bg,
        border: `1px solid ${border}`,
        minWidth: 110,
      }}
    >
      <div
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: color,
          flexShrink: 0,
        }}
      />
      <div>
        <div
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: "#0f172a",
            lineHeight: 1.1,
          }}
        >
          {loading ? (
            <div
              style={{
                width: 28,
                height: 16,
                borderRadius: 4,
                background: "#e2e8f0",
                animation: "cmShimmer 1.4s infinite",
              }}
            />
          ) : (
            count
          )}
        </div>
        <div
          style={{
            fontSize: 11.5,
            color: "#64748b",
            fontWeight: 550,
            marginTop: 2,
            letterSpacing: "0.02em",
          }}
        >
          {label}
        </div>
      </div>
    </div>
  );

  return (
    <div
      style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}
    >
      <Card
        label="Total Enquiries"
        count={total}
        color="#6366f1"
        bg="#f5f3ff"
        border="#e0e7ff"
      />
      <Card
        label="Notified"
        count={notified}
        color="#16a34a"
        bg="#f0fdf4"
        border="#bbf7d0"
      />
      {/* <Card label="Pending"          count={unnotified} color="#d97706" bg="#fffbeb" border="#fde68a"/> */}
      <Card
        label="Registered Users"
        count={withUser}
        color="#0891b2"
        bg="#ecfeff"
        border="#a5f3fc"
      />
    </div>
  );
};

const DetailRow = ({ icon, label, value, mono = false, multiline = false }) => (
  <div
    style={{
      display: "flex",
      gap: 12,
      padding: "11px 0",
      borderBottom: "1px solid #f8fafc",
      alignItems: multiline ? "flex-start" : "center",
    }}
  >
    <div
      style={{
        flexShrink: 0,
        width: 32,
        height: 32,
        borderRadius: 8,
        background: "#f1f5f9",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#64748b",
        marginTop: multiline ? 2 : 0,
      }}
    >
      {icon}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <p
        style={{
          margin: 0,
          fontSize: 11.5,
          color: "#94a3b8",
          fontWeight: 550,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          marginBottom: 2,
        }}
      >
        {label}
      </p>
      <p
        style={{
          margin: 0,
          fontSize: 13.5,
          color: value ? "#0f172a" : "#cbd5e1",
          fontWeight: value ? 500 : 400,
          fontFamily: mono ? "monospace" : "inherit",
          wordBreak: "break-word",
          lineHeight: 1.5,
        }}
      >
        {value || "—"}
      </p>
    </div>
  </div>
);

const ContactDetailView = ({ contactId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const doFetch = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.get(contactId);
        if (cancelled) return;
        setData(data?.data ?? data);
      } catch (err) {
        if (!cancelled)
          setError(
            err?.response?.status === 403
              ? "Access denied."
              : "Network error. Unable to fetch contact details.",
          );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    doFetch();
    return () => {
      cancelled = true;
    };
  }, [contactId]);

  if (loading)
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {Array.from({ length: 7 }).map((_, i) => (
          <div
            key={i}
            style={{ display: "flex", gap: 12, alignItems: "center" }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: "#f1f5f9",
                flexShrink: 0,
              }}
            />
            <div style={{ flex: 1 }}>
              <div
                style={{
                  height: 10,
                  width: 80,
                  borderRadius: 4,
                  background:
                    "linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)",
                  backgroundSize: "200% 100%",
                  animation: "cmShimmer 1.4s infinite",
                  marginBottom: 6,
                }}
              />
              <div
                style={{
                  height: 13,
                  width: `${[160, 200, 130, 100, 180, 110, 90][i]}px`,
                  borderRadius: 4,
                  background:
                    "linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)",
                  backgroundSize: "200% 100%",
                  animation: "cmShimmer 1.4s infinite",
                }}
              />
            </div>
          </div>
        ))}
      </div>
    );

  if (error)
    return (
      <div style={{ textAlign: "center", padding: "32px 20px" }}>
        <div style={{ fontSize: 36, marginBottom: 10 }}>⚠️</div>
        <p
          style={{
            margin: 0,
            fontSize: 15,
            fontWeight: 600,
            color: "#dc2626",
            marginBottom: 6,
          }}
        >
          Failed to Load
        </p>
        <p style={{ margin: 0, fontSize: 13, color: "#94a3b8" }}>{error}</p>
        <button
          onClick={onClose}
          style={{
            marginTop: 16,
            padding: "8px 20px",
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            background: "#fff",
            color: "#64748b",
            fontSize: 14,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Close
        </button>
      </div>
    );

  return (
    <div>
      {/* Header card */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          padding: "14px 16px",
          background: "linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)",
          borderRadius: 10,
          border: "1px solid #e0f2fe",
          marginBottom: 20,
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #3b82f6, #6366f1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <span style={{ fontSize: 20, fontWeight: 700, color: "#fff" }}>
            {(data?.name || "?")[0].toUpperCase()}
          </span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            style={{
              margin: 0,
              fontSize: 16,
              fontWeight: 700,
              color: "#0f172a",
            }}
          >
            {data?.name || "—"}
          </p>
          <p style={{ margin: "2px 0 0", fontSize: 12.5, color: "#64748b" }}>
            {data?.email || "—"}
          </p>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            alignItems: "flex-end",
          }}
        >
          <Badge
            active={data?.is_notified}
            trueLabel="Notified"
            falseLabel="Pending"
            falseColor="#d97706"
            falseBg="#fffbeb"
          />
          {data?.user && (
            <Badge
              active={true}
              trueLabel="Registered User"
              trueColor="#0891b2"
              trueBg="#ecfeff"
            />
          )}
        </div>
      </div>

      {/* Fields */}
      <DetailRow icon={<UserIcon />} label="Full Name" value={data?.name} />
      <DetailRow icon={<MailIcon />} label="Email" value={data?.email} mono />
      {data?.user_email && data?.user_email !== data?.email && (
        <DetailRow
          icon={<MailIcon />}
          label="Account Email"
          value={data?.user_email}
          mono
        />
      )}
      <DetailRow icon={<PhoneIcon />} label="Phone" value={data?.phone} />
      <DetailRow
        icon={<BuildingIcon />}
        label="Company"
        value={data?.company_name}
      />
      <DetailRow
        icon={<MessageIcon />}
        label="Message"
        value={data?.message}
        multiline
      />
      <DetailRow
        icon={<CalendarIcon />}
        label="Submitted At"
        value={data?.created_at}
      />
      {data?.is_notified && (
        <DetailRow
          icon={<BellIcon />}
          label="Notified At"
          value={data?.notified_at}
        />
      )}
    </div>
  );
};

const ContactUs = () => {
  const [allContacts, setAllContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filterNotified, setFilterNotified] = useState(true);
  const [filterHasUser, setFilterHasUser] = useState("");
  const [viewId, setViewId] = useState(null);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.list(search, filterNotified, filterHasUser);

      if (data.status === 401) {
        toast.error("Session Expired", "Please log in again.");
        setLoading(false);
        return;
      }
      if (data.status === 403) {
        toast.error(
          "Access Denied",
          "You don't have permission to view contact enquiries.",
        );
        setLoading(false);
        return;
      }

      setAllContacts(data.results || []);
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error(
          "Access Denied",
          "You don't have permission to view contact enquiries.",
        );
      } else if (err?.name === "TypeError") {
        toast.error(
          "Network Error",
          "Unable to reach the server. Check your connection.",
        );
      } else {
        toast.error(
          "Failed to Load",
          "Unable to fetch contact enquiries. Please try again.",
        );
      }
    } finally {
      setLoading(false);
    }
  }, [search, filterNotified, filterHasUser]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, filterNotified, filterHasUser]);
  useEffect(() => {
    const t = setTimeout(fetchContacts, 400);
    return () => clearTimeout(t);
  }, [fetchContacts]);

  const totalCount = allContacts.length;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const pagedContacts = useMemo(
    () =>
      allContacts.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [allContacts, currentPage],
  );

  const hasFilters = search || filterNotified || filterHasUser;
  const inputBase = {
    padding: "8px 12px",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    fontSize: 14,
    background: "#f8fafc",
    color: "#1e293b",
    outline: "none",
    fontFamily: "inherit",
  };
  const COLS = [
    "Sr No.",
    "Name",
    "Email",
    "Phone",
    "Company",
    "Notified",
    "Has Account",
    "Actions",
  ];

  return (
    <div
      style={{
        fontFamily: "'Inter', -apple-system, sans-serif",
        padding: 24,
        background: "#f8fafc",
        minHeight: "100vh",
        color: "#1e293b",
      }}
    >
      <style>{`
        @keyframes cmShimmer { 0%   { background-position:  200% 0; } 100% { background-position: -200% 0; } }
        @keyframes cmFadeUp  { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes toastIn   { from { opacity: 0; transform: translateX(24px) scale(0.97); } to { opacity: 1; transform: translateX(0) scale(1); } }
        @keyframes toastOut  { from { opacity: 1; transform: translateX(0) scale(1); } to { opacity: 0; transform: translateX(28px) scale(0.95); } }
        @keyframes spin      { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .cu-row:hover        { background: #f8fafc !important; }
        .cu-view-btn:hover   { background: #dbeafe !important; }
        .cu-page-btn:hover   { background: #f1f5f9 !important; border-color: #cbd5e1 !important; }
        * { box-sizing: border-box; }
      `}</style>

      <ToastContainer />

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 24,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: 22,
              fontWeight: 700,
              color: "#0f172a",
            }}
          >
            Contact Us
          </h1>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#64748b" }}>
            Manage and review contact enquiries submitted by users
          </p>
        </div>
      </div>

      <StatsBar contacts={allContacts} loading={loading} />

      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          padding: "14px 18px",
          border: "1px solid #e2e8f0",
          marginBottom: 16,
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "center",
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        }}
      >
        <div style={{ position: "relative", flex: "1 1 200px" }}>
          <span
            style={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
              color: "#94a3b8",
              display: "flex",
              pointerEvents: "none",
            }}
          >
            <SearchIcon />
          </span>
          <input
            style={{
              ...inputBase,
              width: "100%",
              paddingLeft: 34,
              boxSizing: "border-box",
            }}
            placeholder="Search name, email, company…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
            onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
          />
        </div>

        <select
          style={{ ...inputBase, cursor: "pointer", flex: "0 1 155px" }}
          value={filterNotified}
          onChange={(e) => setFilterNotified(e.target.value)}
        >
          <option value="">All Notified</option>
          <option value="true">Notified</option>
          <option value="false">Not Notified</option>
        </select>

        <select
          style={{ ...inputBase, cursor: "pointer", flex: "0 1 155px" }}
          value={filterHasUser}
          onChange={(e) => setFilterHasUser(e.target.value)}
        >
          <option value="">All Users</option>
          <option value="true">Has Account</option>
          <option value="false">No Account</option>
        </select>

        {hasFilters && (
          <button
            onClick={() => {
              setSearch("");
              setFilterNotified("");
              setFilterHasUser("");
            }}
            style={{
              ...inputBase,
              background: "#fff5f5",
              color: "#dc2626",
              border: "1px solid #fecaca",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: 13,
              whiteSpace: "nowrap",
            }}
          >
            Clear
          </button>
        )}
      </div>

      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          border: "1px solid #e2e8f0",
          overflow: "hidden",
          boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
        }}
      >
        <div style={{ overflowX: "auto" }}>
          <table
            style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}
          >
            <thead>
              <tr>
                {COLS.map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: "11px 16px",
                      textAlign: "left",
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      background: "#f8fafc",
                      borderBottom: "1px solid #e2e8f0",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <TableSkeleton />
              ) : allContacts.length === 0 ? (
                <tr>
                  <td colSpan={COLS.length}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        padding: "56px 20px",
                        gap: 8,
                      }}
                    >
                      <p
                        style={{
                          margin: 0,
                          fontSize: 16,
                          fontWeight: 600,
                          color: "#374151",
                        }}
                      >
                        No enquiries found
                      </p>
                      <p style={{ margin: 0, fontSize: 13, color: "#94a3b8" }}>
                        {hasFilters
                          ? "Try adjusting your filters"
                          : "No contact enquiries have been submitted yet"}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                pagedContacts.map((contact, idx) => (
                  <tr
                    key={contact.id}
                    className="cu-row"
                    style={{
                      borderBottom: "1px solid #f1f5f9",
                      transition: "background 0.1s",
                    }}
                  >
                    {/* Sr No. */}
                    <td
                      style={{
                        padding: "13px 16px",
                        color: "#94a3b8",
                        fontSize: 13,
                      }}
                    >
                      {(currentPage - 1) * PAGE_SIZE + idx + 1}
                    </td>
                    {/* Name with avatar */}
                    <td style={{ padding: "13px 16px" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 9,
                        }}
                      >
                        <div
                          style={{
                            width: 30,
                            height: 30,
                            borderRadius: "50%",
                            background:
                              "linear-gradient(135deg, #3b82f6, #6366f1)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 700,
                              color: "#fff",
                            }}
                          >
                            {(contact.name || "?")[0].toUpperCase()}
                          </span>
                        </div>
                        <span style={{ fontWeight: 600, color: "#0f172a" }}>
                          {contact.name || "—"}
                        </span>
                      </div>
                    </td>
                    {/* Email */}
                    <td
                      style={{
                        padding: "13px 16px",
                        color: "#334155",
                        fontFamily: "monospace",
                        fontSize: 13,
                      }}
                    >
                      {contact.email || "—"}
                    </td>
                    {/* Phone */}
                    <td
                      style={{
                        padding: "13px 16px",
                        color: "#64748b",
                        fontFamily: "monospace",
                        fontSize: 13,
                      }}
                    >
                      {contact.phone || "—"}
                    </td>
                    {/* Company */}
                    <td style={{ padding: "13px 16px", color: "#334155" }}>
                      {contact.company_name || "—"}
                    </td>
                    {/* Notified badge */}
                    <td style={{ padding: "13px 16px" }}>
                      <Badge
                        active={contact.is_notified}
                        trueLabel="Notified"
                        falseLabel="Pending"
                        falseColor="#d97706"
                        falseBg="#fffbeb"
                      />
                    </td>
                    {/* Has user badge */}
                    <td style={{ padding: "13px 16px" }}>
                      <Badge
                        active={!!contact.user}
                        trueLabel="Yes"
                        falseLabel="No"
                        trueColor="#0891b2"
                        trueBg="#ecfeff"
                      />
                    </td>
                    {/* Created at */}
                    {/* <td style={{ padding: "13px 16px", color: "#94a3b8", fontSize: 12, whiteSpace: "nowrap" }}>{contact.created_at || "—"}</td> */}
                    {/* Actions */}
                    <td style={{ padding: "13px 16px" }}>
                      <button
                        className="cu-view-btn"
                        title="View Details"
                        onClick={() => setViewId(contact.id)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          padding: "6px 10px",
                          gap: 5,
                          background: "#eff6ff",
                          border: "1px solid #bfdbfe",
                          borderRadius: 6,
                          cursor: "pointer",
                          color: "#3b82f6",
                          fontSize: 12,
                          fontWeight: 600,
                          transition: "background 0.15s",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <EyeIcon /> View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && allContacts.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={(p) => {
              if (p >= 1 && p <= totalPages) setCurrentPage(p);
            }}
          />
        )}
      </div>

      {viewId && (
        <Modal
          title="Contact Enquiry Details"
          onClose={() => setViewId(null)}
          maxWidth={560}
        >
          <ContactDetailView
            contactId={viewId}
            onClose={() => setViewId(null)}
          />
        </Modal>
      )}
    </div>
  );
};

export default ContactUs;
